import { experimentNamesByType } from './experiments-query';
import type { ExperimentType, RegisteredExperiment, Variant } from './typings';

const EU_COUNTRIES = new Set([
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH', // Switzerland: non-EU, but also part
  'NO', // Norway: non-EU, but also part
  'UK', // UK: non-EU, but also part
]);

type ContributedExperiment = {
  name: string;
  supportedVariants: [string, ...string[]];
  experimentType?: ExperimentType;
};

type ExperimentSource = {
  contributes?: {
    experiments?: ContributedExperiment[];
  };
  metadata?: {
    contributes?: {
      experiments?: ContributedExperiment[];
    };
  };
};

export type ExperimentSourceMap = Record<string, ExperimentSource>;

export type ExtractedExperiments = {
  globalRegisteredExperiments: RegisteredExperiment[];
  conditionalRegisteredExperiments: RegisteredExperiment[];
  userExperimentNames: 'NONE' | string[];
  visitorExperimentNames: 'NONE' | string[];
};

function shouldRegisterExperiment(name: string): boolean {
  return name !== 'ABACUS-546';
}

function shouldRegisterConditionalExperiment(
  name: string,
  viewerCountry?: string
): boolean {
  if (
    name === 'ABACUS-546' &&
    (!viewerCountry || EU_COUNTRIES.has(viewerCountry))
  ) {
    return false;
  }

  return true;
}

function filterGlobalRegisteredExperiments(
  experiments: RegisteredExperiment[]
): RegisteredExperiment[] {
  return experiments.filter(({ name }) => shouldRegisterExperiment(name));
}

function filterConditionalRegisteredExperiments(
  experiments: RegisteredExperiment[],
  viewerCountry?: string
): RegisteredExperiment[] {
  return experiments.filter(({ name }) => {
    return (
      !shouldRegisterExperiment(name) &&
      shouldRegisterConditionalExperiment(name, viewerCountry)
    );
  });
}

function filterRegisteredExperimentsForRequest(
  experiments: RegisteredExperiment[],
  viewerCountry?: string
): RegisteredExperiment[] {
  return [
    ...filterGlobalRegisteredExperiments(experiments),
    ...filterConditionalRegisteredExperiments(experiments, viewerCountry),
  ];
}

function getContributes(
  source: ExperimentSource
): ExperimentSource['contributes'] {
  return source.metadata?.contributes ?? source.contributes;
}

function getRegisteredExperiments(
  experimentSourceMap: ExperimentSourceMap
): RegisteredExperiment[] {
  return Object.values(experimentSourceMap).flatMap((source) => {
    const { experiments = [] } = getContributes(source) ?? {};

    return experiments.map(
      ({ experimentType = 'user', supportedVariants, ...rest }) => {
        return {
          experimentType,
          // oxlint-disable-next-line typescript/consistent-type-assertions
          supportedVariants: supportedVariants as ['A', ...Variant[]],
          ...rest,
        };
      }
    );
  });
}

export function extractExperiments(
  experimentSourceMap: ExperimentSourceMap,
  viewerCountry?: string
): ExtractedExperiments {
  const experiments = getRegisteredExperiments(experimentSourceMap);
  const globalRegisteredExperiments =
    filterGlobalRegisteredExperiments(experiments);
  const conditionalRegisteredExperiments =
    filterConditionalRegisteredExperiments(experiments, viewerCountry);
  const registeredExperimentsForRequest = filterRegisteredExperimentsForRequest(
    experiments,
    viewerCountry
  );

  return {
    globalRegisteredExperiments,
    conditionalRegisteredExperiments,
    userExperimentNames: experimentNamesByType(
      registeredExperimentsForRequest,
      'user'
    ),
    visitorExperimentNames: experimentNamesByType(
      registeredExperimentsForRequest,
      'visitor'
    ),
  };
}
